import { useEffect, useRef } from "react";
import Assembly from "../framework/assembly";
import { Bridge } from "../framework/types/modules";
import { LiveBridge } from "../live_bridge";
import FakeBridge from "../fake_bridge";
import React from "react";
import {
  VisualizationProvider,
  useVisualization,
} from "../framework/visualization/react/context";
import { PageFactory } from "../framework/visualization/react/factories/base";

export interface ScriptHostProps {
  workerUrl: string;
  locale?: string;
  standalone?: boolean;
  className?: string;
  factories?: PageFactory[];
}

const FeldsparContent: React.FC<ScriptHostProps> = ({
  workerUrl,
  locale = "en",
  standalone = false,
  className,
  factories = [],
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const assemblyRef = useRef<Assembly | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { setState, state } = useVisualization();

  useEffect(() => {
    if (!containerRef.current) return;

    // Resolve worker URL to absolute path if it's relative
    const resolvedWorkerUrl = workerUrl.startsWith('/') || workerUrl.startsWith('http')
      ? workerUrl
      : new URL(workerUrl, window.location.href).href;

    console.log('[ScriptHostComponent] Creating worker with URL:', resolvedWorkerUrl);
    
    let worker: Worker;
    try {
      console.log('[ScriptHostComponent] Instantiating Worker...');
      worker = new Worker(resolvedWorkerUrl);
      console.log('[ScriptHostComponent] Worker instance created successfully');
      workerRef.current = worker;
      
      // Set up error handler IMMEDIATELY - before anything else
      // This will be overridden by WorkerProcessingEngine later, but we want early error detection
      const earlyErrorHandler = (error: ErrorEvent) => {
        console.error('[ScriptHostComponent] EARLY Worker error detected:', error);
        console.error('[ScriptHostComponent] Error event type:', error.type);
        console.error('[ScriptHostComponent] Error filename:', error.filename);
        console.error('[ScriptHostComponent] Error lineno:', error.lineno);
        console.error('[ScriptHostComponent] Error colno:', error.colno);
        console.error('[ScriptHostComponent] Error message:', error.message);
        if (error.error) {
          console.error('[ScriptHostComponent] Error object:', error.error);
          console.error('[ScriptHostComponent] Error stack:', error.error?.stack);
        }
      };
      
      // Also add message listener to verify worker is communicating
      const earlyMessageHandler = (event: MessageEvent) => {
        console.log('[ScriptHostComponent] EARLY Worker message received:', event.data);
      };
      
      worker.addEventListener('error', earlyErrorHandler);
      worker.addEventListener('message', earlyMessageHandler);
      
      // Try to send a test message to see if worker responds
      setTimeout(() => {
        if (worker) {
          console.log('[ScriptHostComponent] Sending test ping to worker...');
          worker.postMessage({ eventType: 'ping' });
        }
      }, 50);
      
      // Verify worker can receive messages
      setTimeout(() => {
        if (worker) {
          console.log('[ScriptHostComponent] Worker still alive after 100ms');
        }
      }, 100);
      
    } catch (error) {
      console.error('[ScriptHostComponent] Failed to create worker:', error);
      console.error('[ScriptHostComponent] Worker URL was:', resolvedWorkerUrl);
      console.error('[ScriptHostComponent] Base URL:', window.location.href);
      if (error instanceof Error) {
        console.error('[ScriptHostComponent] Error name:', error.name);
        console.error('[ScriptHostComponent] Error message:', error.message);
        console.error('[ScriptHostComponent] Error stack:', error.stack);
      }
      return;
    }

    // Detect if we're in an iframe (window.self !== window.parent means we're embedded)
    const isInIframe = window.self !== window.parent;
    
    const run = (bridge: Bridge, selectedLocale: string = locale) => {
      const assembly = new Assembly(worker, bridge, factories);
      assembly.visualizationEngine.start(
        containerRef.current!,
        selectedLocale,
        setState
      );
      assembly.processingEngine.start();
      assemblyRef.current = assembly;
    };

    // Use LiveBridge only if:
    // 1. Not in standalone mode
    // 2. In production
    // 3. Actually embedded in an iframe (has a parent window)
    if (!standalone && process.env.NODE_ENV === "production" && isInIframe) {
      console.log("Initializing bridge system (embedded in iframe)");
      
      // Fallback timeout: if no live-init message arrives within 2 seconds, use FakeBridge
      fallbackTimeoutRef.current = setTimeout(() => {
        if (!assemblyRef.current) {
          console.warn("LiveBridge timeout - no live-init message received, falling back to FakeBridge");
          run(new FakeBridge());
        }
        fallbackTimeoutRef.current = null;
      }, 2000);
      
      // Wrap run to clear timeout when LiveBridge initializes
      const wrappedRun = (bridge: Bridge, selectedLocale: string = locale) => {
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
        run(bridge, selectedLocale);
      };
      
      LiveBridge.create(window, wrappedRun);
    } else {
      console.log("Running with fake bridge", { standalone, isInIframe, nodeEnv: process.env.NODE_ENV });
      run(new FakeBridge());
    }

    const observer = new ResizeObserver(() => {
      const height = window.document.documentElement.getBoundingClientRect().height;
      window.parent.postMessage({ action: "resize", height }, "*");
    });

    observer.observe(window.document.body);

    // Send a message to the parent window indicating that the app has loaded. This is used
    // to trigger the setup of the channel between the iframe and the parent window.
    window.parent.postMessage({ action: 'app-loaded' }, '*')


    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
      observer.disconnect();
      setTimeout(() => {
        assemblyRef.current?.visualizationEngine.terminate();
        assemblyRef.current?.processingEngine.terminate();
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      }, 0);
    };
  }, [workerUrl, locale, standalone, setState, factories]);

  return (
    <div ref={containerRef} className={className}>
      {state.elements.length > 0 ? (
        state.elements
      ) : (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            padding: "2rem",
            gap: "1rem",
            fontFamily: "system-ui, -apple-system, sans-serif",
            color: "#444",
          }}
        >
          <div
            style={{
              width: "min(560px, 90vw)",
              display: "grid",
              gap: "0.75rem",
            }}
          >
            <div
              style={{
                height: "16px",
                borderRadius: "8px",
                background: "linear-gradient(90deg, #eee, #f5f5f5, #eee)",
                backgroundSize: "200% 100%",
                animation: "feldspar-shimmer 1.2s ease-in-out infinite",
              }}
            />
            <div
              style={{
                height: "12px",
                borderRadius: "8px",
                background: "linear-gradient(90deg, #eee, #f5f5f5, #eee)",
                backgroundSize: "200% 100%",
                animation: "feldspar-shimmer 1.2s ease-in-out infinite",
              }}
            />
            <div
              style={{
                height: "12px",
                width: "70%",
                borderRadius: "8px",
                background: "linear-gradient(90deg, #eee, #f5f5f5, #eee)",
                backgroundSize: "200% 100%",
                animation: "feldspar-shimmer 1.2s ease-in-out infinite",
              }}
            />
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
              Loading the study…
            </div>
            <div style={{ color: "#777" }}>
              Please wait a few seconds.
            </div>
          </div>
          <style>{`
            @keyframes feldspar-shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export const ScriptHostComponent: React.FC<ScriptHostProps> = (props) => (
  <VisualizationProvider>
    <FeldsparContent {...props} />
  </VisualizationProvider>
);
